/**
 * This function takes an array of categories and returns a flattened array of all the tags
 *
 * @param categories
 * @returns an array of all the tags
 *
 * @example
 * const categories = [
 *  {
 *   categoryId: '1',
 *   tags: {
 *    '1': ['tag1', 'tag2'],
 *    '2': ['tag3', 'tag4'],
 *   }
 *  },
 *  {
 *   categoryId: '2',
 *   tags: {
 *    '1': ['tag5', 'tag6'],
 *    '2': ['tag7', 'tag8'],
 *   },
 *  },
 * ]
 *
 * getFlattenCategoryValues(categories)
 * // ['tag1', 'tag2', 'tag3', 'tag4', 'tag5', 'tag6', 'tag7', 'tag8']
 */
interface ICategory {
  categoryId: string | number
  tags: Record<string, string[]>
}
export function getFlattenCategoryValues(categories?: ICategory[]) {
  if (!categories) return []

  return categories.flatMap(category => Object.values(category.tags).flatMap(tagArray => tagArray))
}

/**
 * Searches for an item in two arrays. Starts with the smaller one,
 * and returns the first item that satisfies the callback condition.
 * If the item is found in the smaller array, stops searching immediately.
 *
 * @template T - The type of items in the arrays
 * @param {T[]} array1 - The first array to search
 * @param {T[]} array2 - The second array to search
 * @param {(item: T, index: number) => boolean} callback - The callback function to test each item in the arrays
 * @returns {T | undefined} The first item that satisfies the callback condition, or undefined if no such item is found
 *
 * @example
 * interface ExampleObject {
 *   id: string;
 *   name: string;
 * }
 *
 * const array1: ExampleObject[] = [
 *   { id: '1', name: 'Object 1' },
 *   { id: '2', name: 'Object 2' },
 *   { id: '3', name: 'Object 3' }
 * ];
 * const array2: ExampleObject[] = [
 *   { id: '4', name: 'Object 4' },
 *   { id: '5', name: 'Object 5' },
 *   { id: '6', name: 'Object 6' }
 * ];
 *
 * const callback = (item: ExampleObject, index: number) => item.id === '2';
 *
 * const foundObject = searchInArrays(array1, array2, callback);
 * console.log(foundObject); // Output: { id: '2', name: 'Object 2' }
 */
export function findInArrays<T>(
  array1: T[],
  array2: T[],
  callback: (item: T, index: number) => boolean,
): T | undefined {
  const smallerArray = array1.length <= array2.length ? array1 : array2
  const largerArray = array1.length > array2.length ? array1 : array2

  let foundObject: T | undefined

  smallerArray.some((item, index) => {
    if (callback(item, index)) {
      foundObject = item
      return true // Stops iteration once a match is found
    }

    return false
  })

  if (!foundObject) {
    largerArray.some((item, index) => {
      if (!smallerArray.includes(item) && callback(item, index)) {
        foundObject = item
        return true // Stops iteration once a match is found
      }
      return false
    })
  }

  return foundObject
}
